import type { ApiResponse } from "@internal/backstage-plugin-utility-common";

import type {
    CommitPushComplianceInfo,
    GitHubReleasesAndTags,
    PullRequestComplianceInfo,
    RepoCustomProperty,
} from "../../types";
import {
    GitHubBaseClient,
    type GitHubClientRequestOptions,
} from "../GitHubBaseClient";

/**
 * Sub-client for GitHub GraphQL-backed endpoints.
 *
 * Mounted automatically onto {@link GitHubClient} at the `graphql` property
 * by `ClientRegistry` when `github-common` is evaluated.
 *
 * @public
 */
export class GitHubGraphQLClient extends GitHubBaseClient {
    /**
     * Fetches all custom property values for a repository.
     *
     * @param owner - The GitHub org/user that owns the repo (e.g., "Banno")
     * @param repo - The repository name (e.g., "backstage")
     * @param options - Optional request options (e.g., `githubToken` for user-scoped calls)
     * @returns ApiResponse with an array of custom property name/value pairs
     */
    getRepoCustomPropertiesValues(
        owner: string,
        repo: string,
        options?: GitHubClientRequestOptions
    ): Promise<ApiResponse<RepoCustomProperty[]>> {
        return this.api<RepoCustomProperty[]>(
            `graphql/repos/${owner}/${repo}/properties/values`,
            { method: "GET", headers: this.buildGitHubHeaders(options) }
        );
    }

    /**
     * Checks pull request merge compliance.
     *
     * @param owner - The GitHub org/user that owns the repo (e.g., "Banno")
     * @param repo - The repository name (e.g., "backstage")
     * @param pullNumber - The pull request number
     * @param options - Optional request options (e.g., `githubToken` for user-scoped calls)
     * @returns ApiResponse with `approved` and `commitAuthors`
     */
    pullRequestMergeComplianceCheck(
        owner: string,
        repo: string,
        pullNumber: number,
        options?: GitHubClientRequestOptions
    ): Promise<ApiResponse<PullRequestComplianceInfo>> {
        return this.api<PullRequestComplianceInfo>(
            `graphql/repos/${owner}/${repo}/pulls/${pullNumber}/merge-compliance`,
            { method: "GET", headers: this.buildGitHubHeaders(options) }
        );
    }

    /**
     * Checks commit push compliance.
     *
     * @param owner - The GitHub org/user that owns the repo (e.g., "Banno")
     * @param repo - The repository name (e.g., "backstage")
     * @param commitSha - The SHA of the head commit from the push event
     * @param options - Optional request options (e.g., `githubToken` for user-scoped calls)
     * @returns ApiResponse with `type` and `prs` array
     */
    commitPushComplianceCheck(
        owner: string,
        repo: string,
        commitSha: string,
        options?: GitHubClientRequestOptions
    ): Promise<ApiResponse<CommitPushComplianceInfo>> {
        return this.api<CommitPushComplianceInfo>(
            `graphql/repos/${owner}/${repo}/commits/${commitSha}/push-compliance`,
            { method: "GET", headers: this.buildGitHubHeaders(options) }
        );
    }

    /**
     * Fetches all releases and tags for a repository.
     *
     * @param owner - The GitHub org/user that owns the repo (e.g., "Banno")
     * @param repo - The repository name (e.g., "backstage")
     * @param options - Optional request options (e.g., `githubToken` for user-scoped calls)
     * @returns ApiResponse with releases and tags arrays
     */
    getReleasesAndTags(
        owner: string,
        repo: string,
        options?: GitHubClientRequestOptions
    ): Promise<ApiResponse<GitHubReleasesAndTags>> {
        return this.api<GitHubReleasesAndTags>(
            `graphql/repos/${owner}/${repo}/releases-and-tags`,
            { method: "GET", headers: this.buildGitHubHeaders(options) }
        );
    }
}
